import { useState, useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

/* Loops through `count` steps: first advance after `first` ms, then every `every` ms.
   Runs from mount; pauses only when the section is fully off-screen, when the tab is
   hidden, or for `hold` ms after a manual pick. */
export const useAutoPlay = (count, { first = 3000, every = 3500, hold = 8000 } = {}) => {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const timers = useRef({});
  const holdTimer = useRef(null);
  const firstRun = useRef(true);
  const [active, setActive] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [held, setHeld] = useState(false);
  const [offscreen, setOffscreen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const isSnap = typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

  useEffect(() => { if (!isSnap) setMounted(true); }, [isSnap]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => setOffscreen(!e.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const run = !userPaused && !held && !offscreen && !reduced && !isSnap;

  useEffect(() => {
    const stop = () => {
      clearTimeout(timers.current.t);
      clearInterval(timers.current.i);
    };
    if (!run) {
      stop();
      return stop;
    }
    const start = () => {
      stop();
      /* Full `first` delay only on the very first run; resuming is near-instant. */
      const delay = firstRun.current ? first : 400;
      firstRun.current = false;
      timers.current.t = setTimeout(() => {
        setActive((a) => (a + 1) % count);
        timers.current.i = setInterval(() => setActive((a) => (a + 1) % count), every);
      }, delay);
    };
    start();
    const onVisibility = () => (document.visibilityState === "visible" ? start() : stop());
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [run, count, first, every]);

  useEffect(() => () => clearTimeout(holdTimer.current), []);

  /* Temporary hold after a manual pick; never overrides an explicit user pause. */
  const pause = () => {
    if (reduced || userPaused) return;
    setHeld(true);
    clearTimeout(holdTimer.current);
    holdTimer.current = setTimeout(() => setHeld(false), hold);
  };

  const pick = (i) => {
    setActive(i);
    pause();
  };

  /* Explicit user control: stays paused until the user presses play again. */
  const toggle = () => {
    clearTimeout(holdTimer.current);
    setHeld(false);
    setUserPaused((p) => !p);
  };

  return { ref, active, playing: !userPaused, pause, pick, toggle, reduced, mounted };
};
